/**
 * Workshop — work-centric view of all services across the collection.
 *
 * Three sections:
 *   1. On The Bench  — status="In Progress"  (most prominent)
 *   2. Queued        — status="Logged"
 *   (Bench + Queued sorted by started_at || created_at, oldest first by default)
 *   3. Recently Done — status="Complete", last 10
 *
 * Parts are surfaced as a status pill summary per service (deep-linked to
 * the parts page filtered by that watch). The unit of work is the service.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchServices, startService, completeService, deleteService,
  fetchParts, fetchValidation, fileUrl,
} from "@/lib/api";
import ServiceFormSheet from "@/components/ServiceFormSheet";
import { StatusChip } from "@/components/ServicesTable";
import SearchableCombobox from "@/components/SearchableCombobox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import {
  Wrench, Play, CheckCircle2, Plus, ArrowUpRight, ChevronDown, ChevronRight,
  Pencil, Trash2, ListChecks, Clock, AlertTriangle, Package, ArrowUpDown, Watch,
} from "lucide-react";

const SORT_KEY = "tickdoc_workshop_sort";
const DAYS_KEY = "tickdoc_workshop_days_badge";
const STALE_DAYS = 60;
const DAYS_STATUSES = /^(in progress|waiting on parts)$/i;
const beganAt = (s) => s.started_at || s.created_at || "";
const daysEligible = (s) => DAYS_STATUSES.test(s.status || "") || DAYS_STATUSES.test(s.watch_status || "");
const isStale = (s) => daysEligible(s) && (daysSince(beganAt(s)) ?? -1) >= STALE_DAYS;

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const fmtMoney = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 86400000);
}

function relTime(iso) {
  const d = daysSince(iso);
  if (d === null) return "—";
  if (d === 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} yr ago`;
}

const PART_TONE = {
  Needed: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  Ordered: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  Shipped: "bg-violet-500/15 text-violet-700 border-violet-500/30",
  Backorder: "bg-rose-500/15 text-rose-700 border-rose-500/30",
  Received: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
};

const OPEN_PART_STATUSES = ["Needed", "Ordered", "Shipped", "Backorder"];

export default function Workshop() {
  const [services, setServices] = useState([]);
  const [partsByWatch, setPartsByWatch] = useState({}); // { watch_id: [{order_status, total_cost}, ...] }
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [completeStatus, setCompleteStatus] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [validation, setValidation] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortDir, setSortDir] = useState(() => localStorage.getItem(SORT_KEY) || "asc");
  const toggleSort = () => setSortDir((d) => {
    const next = d === "asc" ? "desc" : "asc";
    localStorage.setItem(SORT_KEY, next);
    return next;
  });
  const [showDays, setShowDays] = useState(() => localStorage.getItem(DAYS_KEY) !== "off");
  const [staleOnly, setStaleOnly] = useState(false);
  const toggleDays = () => setShowDays((v) => {
    localStorage.setItem(DAYS_KEY, v ? "off" : "on");
    return !v;
  });
  const byBegan = useCallback(
    (a, b) => (sortDir === "asc" ? 1 : -1) * (beganAt(a).localeCompare(beganAt(b)) || (a.id || "").localeCompare(b.id || "")),
    [sortDir]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch services first — this is the source of truth for the workshop.
      // Parts are only used to compute the pill summary, so a parts failure
      // must NOT take down the services display.
      const svc = await fetchServices({ limit: 1000, sort: "service_date", order: "asc" });
      setServices(svc.items || []);
      try {
        const prt = await fetchParts({ limit: 1000, sort: "order_date", order: "desc" });
        const grouped = {};
        for (const p of prt.items || []) {
          if (!p.watch_id) continue;
          (grouped[p.watch_id] = grouped[p.watch_id] || []).push(p);
        }
        setPartsByWatch(grouped);
      } catch (err) {
        // Parts pill summary is non-critical — log but don't block the bench.
        // eslint-disable-next-line no-console
        console.warn("Failed to load parts for workshop pill summary", err);
        setPartsByWatch({});
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load workshop"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchValidation().then(setValidation).catch(() => {}); }, []);

  // Workshop buckets are now status-agnostic so newly added custom statuses
  // (e.g. "Testing", "Waiting on parts") show up automatically without code
  // changes. Rule of thumb:
  //   "Logged"   → Queued column
  //   "Complete" → Recently completed
  //   anything else → On The Bench (it's active work somewhere in the flow)
  const onBench = useMemo(
    () => services
      .filter((s) => {
        const st = s.status || "Logged";
        return st !== "Logged" && st !== "Complete";
      })
      .sort(byBegan),
    [services, byBegan]
  );
  const benchShown = useMemo(() => (staleOnly ? onBench.filter(isStale) : onBench), [onBench, staleOnly]);
  const queued = useMemo(
    () => services.filter((s) => (s.status || "Logged") === "Logged").sort(byBegan),
    [services, byBegan]
  );
  const completed = useMemo(
    () => services.filter((s) => s.status === "Complete")
      .sort((a, b) => (b.completed_at || b.service_date || "").localeCompare(a.completed_at || a.service_date || "")),
    [services]
  );

  const completedThisMonth = useMemo(() => {
    const now = new Date();
    const yy = now.getFullYear(); const mm = now.getMonth();
    return completed.filter((s) => {
      const d = s.completed_at ? new Date(s.completed_at) : null;
      return d && d.getFullYear() === yy && d.getMonth() === mm;
    }).length;
  }, [completed]);

  const handleStart = async (s) => {
    try {
      await startService(s.id);
      toast.success("Service started — watch flipped to Being Serviced");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Start failed"));
    }
  };

  const handleComplete = async () => {
    if (!completing) return;
    try {
      await completeService(completing.id, {
        watch_status: completeStatus || null,
        completion_notes: completeNotes || null,
      });
      toast.success(completeStatus
        ? `Service complete — watch is now ${completeStatus}`
        : "Service marked complete");
      setCompleting(null); setCompleteStatus(""); setCompleteNotes("");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Complete failed"));
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteService(confirmDel.id);
      toast.success("Service removed");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]" data-testid="workshop-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Workshop</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">The Bench</h1>
          <div className="text-sm text-[#3A2A10] mt-3 font-mono-data">
            {loading ? "…" : `${onBench.length} on the bench · ${queued.length} queued · ${completedThisMonth} completed this month`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleSort}
            className="inline-flex items-center gap-2 px-3 h-9 whitespace-nowrap text-xs uppercase tracking-wider border border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#0A0604] rounded-sm transition-colors"
            data-testid="workshop-sort-toggle"
            title="Sort by date started / logged"
          >
            <ArrowUpDown size={14} /> {sortDir === "asc" ? "Oldest first" : "Newest first"}
          </button>
          <button
            onClick={toggleDays}
            className={`inline-flex items-center gap-2 px-3 h-9 whitespace-nowrap text-xs uppercase tracking-wider border rounded-sm transition-colors ${showDays ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#0A0604]" : "border-[#8B6A1F]/45 text-[#3A2A10] hover:border-[#8B6A1F]/60"}`}
            data-testid="workshop-days-toggle"
            aria-pressed={showDays}
            title={`Show days on bench (amber after ${STALE_DAYS} days)`}
          >
            <Clock size={14} /> {showDays ? "Days: on" : "Days: off"}
          </button>
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="inline-flex items-center gap-2 px-3 h-9 whitespace-nowrap text-xs uppercase tracking-wider bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] rounded-sm"
            data-testid="workshop-add-service"
          >
            <Plus size={14} /> Add Service
          </button>
          <Link
            to="/parts"
            className="inline-flex items-center gap-2 px-3 h-9 whitespace-nowrap text-xs uppercase tracking-wider border border-[#8B6A1F]/45 hover:border-[#8B6A1F]/60 text-[#0A0604] rounded-sm transition-colors"
            data-testid="workshop-goto-parts"
          >
            <Package size={14} /> Parts &amp; Supplies
          </Link>
        </div>
      </header>

      {loading && (
        <div className="text-xs uppercase tracking-[0.25em] text-[#3A2A10] py-20 text-center" data-testid="workshop-loading">
          Loading the bench…
        </div>
      )}

      {!loading && (
        <>
          {staleOnly && (
            <div
              className="mb-5 inline-flex items-center gap-3 px-3 py-2 border border-amber-500/50 bg-amber-500/15 text-amber-900 rounded-sm text-[11px] uppercase tracking-wider"
              data-testid="workshop-stale-filter-chip"
            >
              <Clock size={12} /> Showing {STALE_DAYS}+ day jobs · {benchShown.length}
              <button
                onClick={() => setStaleOnly(false)}
                className="underline underline-offset-2 hover:text-[#0A0604]"
                data-testid="workshop-stale-filter-clear"
              >
                Clear
              </button>
            </div>
          )}
          {/* On The Bench ----------------------------------------------- */}
          <Section
            title="On The Bench"
            subtitle={onBench.length === 0 ? "Nothing in progress" : staleOnly ? `${benchShown.length} of ${onBench.length} on the bench over ${STALE_DAYS} days` : `${onBench.length} ${onBench.length === 1 ? "watch" : "watches"} actively being worked on`}
            icon={Wrench}
            testid="workshop-section-bench"
          >
            {benchShown.length === 0 ? (
              <EmptyState
                icon={Wrench}
                title={staleOnly ? "No stale jobs" : "Bench is clear"}
                hint={staleOnly ? `Nothing has been on the bench over ${STALE_DAYS} days. Clear the filter to see all jobs.` : "Click Start on a queued service below to bring a watch up onto the bench."}
                testid="workshop-bench-empty"
              />
            ) : (
              <div className="space-y-3">
                {benchShown.map((s) => (
                  <BenchCard
                    key={s.id}
                    svc={s}
                    parts={partsByWatch[s.watch_id] || []}
                    showDays={showDays || staleOnly}
                    onStaleClick={() => { setStaleOnly(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    onComplete={() => setCompleting(s)}
                    onEdit={() => { setEditing(s); setFormOpen(true); }}
                    onDelete={() => setConfirmDel(s)}
                  />
                ))}
              </div>
            )}
          </Section>

          {!staleOnly && (<>
          {/* Queued ----------------------------------------------------- */}
          <Section
            title="Queued"
            subtitle={queued.length === 0 ? "Nothing planned" : `${queued.length} ${queued.length === 1 ? "service" : "services"} waiting · ${sortDir === "asc" ? "oldest" : "newest"} first`}
            icon={ListChecks}
            testid="workshop-section-queued"
            className="mt-12"
          >
            {queued.length === 0 ? (
              <EmptyState
                icon={ListChecks}
                title="Queue is empty"
                hint="Log a service from a watch detail page or hit Add Service above."
                testid="workshop-queued-empty"
              />
            ) : (
              <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] divide-y divide-[#8B6A1F]/25">
                {queued.map((s) => (
                  <QueuedRow
                    key={s.id}
                    svc={s}
                    parts={partsByWatch[s.watch_id] || []}
                    onStart={() => handleStart(s)}
                    onEdit={() => { setEditing(s); setFormOpen(true); }}
                    onDelete={() => setConfirmDel(s)}
                  />
                ))}
              </div>
            )}
          </Section>

          {/* Recently Completed ---------------------------------------- */}
          <Section
            title="Recently Completed"
            subtitle={completed.length === 0 ? "Nothing completed yet" : `${completed.length} total · ${completedThisMonth} this month`}
            icon={CheckCircle2}
            testid="workshop-section-completed"
            className="mt-12"
            collapsible
            collapsed={!showCompleted}
            onToggle={() => setShowCompleted((v) => !v)}
          >
            {showCompleted && (
              completed.length === 0 ? (
                <EmptyState
                  icon={CheckCircle2}
                  title="Nothing finished yet"
                  hint="Completed services will land here as a running log."
                  testid="workshop-completed-empty"
                />
              ) : (
                <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] divide-y divide-[#8B6A1F]/25">
                  {completed.slice(0, 10).map((s) => (
                    <CompletedRow
                      key={s.id}
                      svc={s}
                      onEdit={() => { setEditing(s); setFormOpen(true); }}
                    />
                  ))}
                </div>
              )
            )}
          </Section>
          </>)}
        </>
      )}

      {/* Add / Edit form ---------------------------------------------- */}
      <ServiceFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        service={editing}
        watchId={editing?.watch_id || null}
        onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
      />

      {/* Complete dialog ---------------------------------------------- */}
      <Dialog open={!!completing} onOpenChange={(o) => { if (!o) { setCompleting(null); setCompleteStatus(""); setCompleteNotes(""); } }}>
        <DialogContent className="bg-[#FBF3D9] border-[#8B6A1F]/45 rounded-sm max-w-md" data-testid="workshop-complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0A0604]">Complete service</DialogTitle>
            <DialogDescription className="text-[#3A2A10]">
              Marking this service complete. What's the watch's new status? Leave blank to keep it where it is.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">New watch status</div>
              <SearchableCombobox
                value={completeStatus}
                onChange={setCompleteStatus}
                options={validation["Status"] || []}
                testid="workshop-complete-status"
                placeholder="No change"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["Overhauled", "For Sale", "Running", "Owned"].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setCompleteStatus(q)}
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${completeStatus === q ? "bg-[#D4AF37] text-[#0A0604] border-[#D4AF37]" : "border-[#8B6A1F]/35 text-[#3A2A10] hover:bg-[#2B2214]/[0.06]"}`}
                    data-testid={`workshop-complete-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">Completion notes (optional)</div>
              <textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                rows={3}
                placeholder="Amplitude, rate numbers, parts swapped…"
                className="w-full px-3 py-2 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37] resize-none"
                data-testid="workshop-complete-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setCompleting(null); setCompleteStatus(""); setCompleteNotes(""); }}
              className="px-4 h-10 border border-[#8B6A1F]/45 rounded-sm text-[#3A2A10] hover:bg-[#2B2214]/[0.06] text-xs uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              onClick={handleComplete}
              className="px-4 h-10 bg-emerald-700 hover:bg-emerald-800 text-white text-xs uppercase tracking-wider rounded-sm"
              data-testid="workshop-complete-confirm"
            >
              Mark Complete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm ---------------------------------------------- */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove service?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              This permanently deletes the service record. Parts ordered for this watch are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="workshop-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------- presentation helpers ---------- */

function Section({ title, subtitle, icon: Icon, testid, className = "", children, collapsible, collapsed, onToggle }) {
  return (
    <section className={className} data-testid={testid}>
      <header className="flex items-end justify-between mb-4 gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="font-serif-display text-2xl text-[#0A0604] flex items-center gap-2">
            <Icon size={18} className="text-[#3A2A10]" strokeWidth={1.5} />
            {title}
          </h2>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{subtitle}</span>
        </div>
        {collapsible && (
          <button
            onClick={onToggle}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] hover:text-[#0A0604]"
            data-testid={`${testid}-toggle`}
          >
            {collapsed ? <><ChevronRight size={12} /> Show</> : <><ChevronDown size={12} /> Hide</>}
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 py-14 text-center" data-testid={testid}>
      <Icon size={22} className="mx-auto text-[#0A0604] mb-3" strokeWidth={1.3} />
      <div className="font-serif-display text-xl text-[#0A0604] mb-1">{title}</div>
      <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">{hint}</div>
    </div>
  );
}

function PartsPills({ parts, watchId }) {
  if (!parts || parts.length === 0) {
    return <span className="text-[10px] uppercase tracking-wider text-[#3A2A10]/60">No parts logged</span>;
  }
  const counts = {};
  for (const p of parts) {
    const s = p.order_status || "—";
    counts[s] = (counts[s] || 0) + 1;
  }
  const ordered = ["Needed", "Backorder", "Ordered", "Shipped", "Received"];
  return (
    <div className="flex flex-wrap items-center gap-1">
      {ordered.filter((s) => counts[s]).map((s) => (
        <Link
          key={s}
          to={`/parts?watch_id=${watchId}&status=${encodeURIComponent(s)}`}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${PART_TONE[s] || "border-[#8B6A1F]/45 text-[#3A2A10]"}`}
          data-testid={`workshop-parts-${watchId}-${s}`}
        >
          {s} <span className="font-mono-data">{counts[s]}</span>
        </Link>
      ))}
    </div>
  );
}

function WatchHeading({ svc }) {
  const nav = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); nav(`/inventory/${svc.watch_id}`); }}
      className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left min-w-0 max-w-full hover:opacity-80 transition-opacity"
      data-testid={`workshop-watch-link-${svc.id}`}
    >
      <span className="font-mono-data text-sm text-[#3A2A10] shrink-0">#{svc.watch_excel_id ?? "—"}</span>
      <span className="text-sm text-[#0A0604] min-w-0 break-words">
        {svc.watch_brand || "Unbranded"}
        {svc.watch_model ? ` ${svc.watch_model}` : ""}
        {svc.watch_caliber ? <span className="text-[#3A2A10]"> · {svc.watch_caliber}</span> : null}
        <ArrowUpRight size={11} className="inline ml-1 text-[#3A2A10] align-baseline" />
      </span>
    </button>
  );
}

function WatchThumb({ svc, size = "w-16 h-16" }) {
  const nav = useNavigate();
  const src = fileUrl(svc.watch_photo, 320);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); nav(`/inventory/${svc.watch_id}`); }}
      className={`${size} shrink-0 overflow-hidden rounded-sm border border-[#8B6A1F]/35 bg-[#2B2214]/[0.06] flex items-center justify-center hover:border-[#D4AF37] transition-colors`}
      data-testid={`workshop-thumb-${svc.id}`}
      aria-label="Open watch"
    >
      {src
        ? <img src={src} alt="Watch front" loading="lazy" className="w-full h-full object-cover" />
        : <Watch size={18} className="text-[#3A2A10]/50" strokeWidth={1.3} />}
    </button>
  );
}

function BeganDate({ svc }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-mono-data" data-testid={`workshop-began-${svc.id}`}>
      {svc.started_at ? "Started" : "Logged"} {fmtDate(beganAt(svc))}
    </div>
  );
}

function DaysBadge({ svc, onStaleClick }) {
  const d = daysSince(beganAt(svc));
  if (!daysEligible(svc) || d === null || d < 0) return null;
  const stale = d >= STALE_DAYS;
  const cls = `inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm font-mono-data ${stale ? "border-amber-500/50 text-amber-800 bg-amber-500/20 hover:bg-amber-500/35 transition-colors" : "border-[#8B6A1F]/35 text-[#3A2A10] bg-[#2B2214]/[0.04]"}`;
  const body = <><Clock size={10} /> {d} {d === 1 ? "day" : "days"}</>;
  if (stale) {
    return (
      <button type="button" onClick={onStaleClick} className={cls} data-testid={`workshop-days-${svc.id}`} data-stale="true"
        title={`On the bench over ${STALE_DAYS} days — tap to show only these`}>
        {body}
      </button>
    );
  }
  return (
    <span className={cls} data-testid={`workshop-days-${svc.id}`} data-stale="false" title="Days on the bench">{body}</span>
  );
}

function BenchCard({ svc, parts, showDays, onStaleClick, onComplete, onEdit, onDelete }) {
  const openParts = parts.filter((p) => OPEN_PART_STATUSES.includes(p.order_status));
  const blocked = openParts.length > 0 && openParts.some((p) => ["Needed", "Backorder"].includes(p.order_status));
  return (
    <article
      className="bg-[#FBF3D9] border border-[#D4AF37]/40 px-5 py-4 hover:border-[#D4AF37]/70 transition-colors"
      data-testid={`workshop-bench-card-${svc.id}`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
        <WatchThumb svc={svc} size="w-20 h-20 sm:w-24 sm:h-24" />
        <div className="min-w-0 flex-1">
          <BeganDate svc={svc} />
          <div className="flex items-center gap-3 mb-2 mt-1 flex-wrap">
            <WatchHeading svc={svc} />
            <StatusChip status={svc.status} />
            {showDays && <DaysBadge svc={svc} onStaleClick={onStaleClick} />}
            {blocked && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-rose-500/40 text-rose-700 bg-rose-500/10 rounded-sm"
                data-testid={`workshop-blocked-${svc.id}`}
                title="Open parts needed or on backorder"
              >
                <AlertTriangle size={10} /> Blocked on parts
              </span>
            )}
          </div>
          <div className="font-serif-display text-2xl text-[#0A0604] leading-tight">
            {svc.service_type || "Service"}
          </div>
          {svc.description && (
            <div className="text-sm text-[#3A2A10] mt-1 whitespace-pre-wrap break-words">{svc.description}</div>
          )}
          {svc.notes && (
            <div
              className="mt-2 px-2.5 py-1.5 text-[12px] text-[#3A2A10] bg-[#FBF3D9]/70 border-l-2 border-[#D4AF37]/50 whitespace-pre-wrap"
              data-testid={`workshop-bench-notes-${svc.id}`}
              title="Notes"
            >
              {svc.notes}
            </div>
          )}
          {svc.cost ? (
            <div className="mt-3 text-[11px] text-[#3A2A10] font-mono-data">
              {fmtMoney(svc.cost)}
            </div>
          ) : null}
          <div className="mt-3"><PartsPills parts={parts} watchId={svc.watch_id} /></div>
        </div>
        </div>
        <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-[#D4AF37]/20 -mx-1">
          <IconBtn label="Edit" testid={`workshop-bench-edit-${svc.id}`} onClick={onEdit}><Pencil size={13} /></IconBtn>
          <IconBtn label="Delete" testid={`workshop-bench-delete-${svc.id}`} onClick={onDelete} danger><Trash2 size={13} /></IconBtn>
          <button
            onClick={onComplete}
            className="inline-flex items-center gap-1 px-3 h-9 ml-1 text-[10px] uppercase tracking-[0.2em] bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm"
            data-testid={`workshop-bench-complete-${svc.id}`}
          >
            <CheckCircle2 size={12} /> Complete
          </button>
        </div>
      </div>
    </article>
  );
}

function QueuedRow({ svc, parts, onStart, onEdit, onDelete }) {
  const overdue = svc.service_date && svc.service_date < new Date().toISOString().slice(0, 10);
  // A sold watch shouldn't be eligible to start workshop work — the backend
  // already guards against status overrides on SOLD, so the Start button
  // would be a silent no-op. Surface the state clearly instead.
  const watchSold = /sold/i.test(svc.watch_status || "");
  return (
    <div
      className="flex flex-wrap items-center gap-4 px-5 py-3.5 hover:bg-[#2B2214]/[0.04] transition-colors"
      data-testid={`workshop-queued-row-${svc.id}`}
    >
      <WatchThumb svc={svc} size="w-12 h-12" />
      <div className="min-w-[100px] font-mono-data text-xs">
        <BeganDate svc={svc} />
        {svc.service_date && (
          <div className={`mt-0.5 ${overdue ? "text-rose-700" : "text-[#0A0604]"}`} data-testid={`workshop-queued-date-${svc.id}`}>
            Due {fmtDate(svc.service_date)}
          </div>
        )}
        {overdue && <div className="text-[9px] uppercase tracking-wider text-rose-700/80">overdue</div>}
      </div>
      <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
        <WatchHeading svc={svc} />
        <div className="text-sm text-[#0A0604] mt-0.5 break-words">
          {svc.service_type || "Service"}
          {svc.description && <span className="text-[#3A2A10]"> · {svc.description}</span>}
        </div>
      </div>
      <PartsPills parts={parts} watchId={svc.watch_id} />
      <div className="flex items-center gap-1.5 ml-auto">
        {watchSold ? (
          <span
            className="inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-[0.2em] bg-[#2B2214]/[0.08] border border-[#8B6A1F]/35 text-[#3A2A10] rounded-sm cursor-not-allowed"
            title="Watch is sold — cannot start workshop work. Edit the watch status first if this is wrong."
            data-testid={`workshop-queued-sold-${svc.id}`}
          >
            Sold
          </span>
        ) : (
          <button
            onClick={onStart}
            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.2em] bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] rounded-sm"
            data-testid={`workshop-queued-start-${svc.id}`}
            title="Start service (flips watch to Being Serviced)"
          >
            <Play size={11} /> Start
          </button>
        )}
        <IconBtn label="Edit" testid={`workshop-queued-edit-${svc.id}`} onClick={onEdit}><Pencil size={13} /></IconBtn>
        <IconBtn label="Delete" testid={`workshop-queued-delete-${svc.id}`} onClick={onDelete} danger><Trash2 size={13} /></IconBtn>
      </div>
    </div>
  );
}

function CompletedRow({ svc, onEdit }) {
  return (
    <div
      className="flex flex-wrap items-center gap-4 px-5 py-3 hover:bg-[#2B2214]/[0.04] transition-colors"
      data-testid={`workshop-completed-row-${svc.id}`}
    >
      <WatchThumb svc={svc} size="w-10 h-10" />
      <div className="min-w-[110px] font-mono-data text-xs text-[#3A2A10]">
        {svc.completed_at ? svc.completed_at.slice(0, 10) : svc.service_date || "—"}
      </div>
      <div className="min-w-0 flex-1">
        <WatchHeading svc={svc} />
        <div className="text-sm text-[#0A0604] mt-0.5 break-words">
          {svc.service_type || "Service"}
          {svc.description && <span className="text-[#3A2A10]"> · {svc.description}</span>}
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono-data">
        {relTime(svc.completed_at)}
      </div>
      <IconBtn label="Edit" testid={`workshop-completed-edit-${svc.id}`} onClick={onEdit}><Pencil size={13} /></IconBtn>
    </div>
  );
}

function IconBtn({ children, label, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      className={`p-1.5 transition-colors ${danger ? "text-[#3A2A10] hover:text-rose-600" : "text-[#3A2A10] hover:text-[#0A0604]"}`}
      aria-label={label}
      title={label}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
